import {defu} from 'defu'
import SiteUtil from "../utils/siteUtil.js";


export const useApi = async (url, body, options = {}, option = 1, contentType = `application/json`) => {
    const config = useRuntimeConfig()
    const store = useUserStore()
    const router = useRouter()

    const baseURL = config.public.dripApiServer
    if (!baseURL) {
        console.warn('[useApi] dripApiServer is not configured! config.public:', config.public)
    }

    const originalUrl = String(url)
    const isDeviceBoundEndpoint =
        originalUrl.startsWith('/api/devices/') ||
        originalUrl === '/api/devices/register' ||
        originalUrl.startsWith('/api/payment-terminals/') ||
        originalUrl.startsWith('/api/pos/orders') ||
        originalUrl.startsWith('/api/pos/kitchen-print-jobs')
    let deviceAuthToken = null
    if (isDeviceBoundEndpoint && typeof window !== 'undefined') {
        deviceAuthToken = window.__STARVLY_DEVICE_AUTH_TOKEN__ || null
        if (!deviceAuthToken) {
            try {
                deviceAuthToken = window.localStorage?.getItem('deviceAuthToken') || null
            } catch {
                // Tauri keeps this value sealed in SQLite and publishes it to
                // the in-memory window property during device registration.
            }
        }
    }

    url = SiteUtil.appendDateToUrl(url)
    const deviceHeaders = deviceAuthToken ? {'X-POS-Device-Token': deviceAuthToken} : {}
    const defaults = {
        baseURL,
        key: toValue(url),
        headers: store.httpToken ? {
            Authorization: `Bearer ${store?.httpToken?.token}`,
            'Cache-Control': 'no-cache',
            ...deviceHeaders,
        } : {
            'Cache-Control': 'no-cache',
            ...deviceHeaders,
        },
        cache: 'no-cache',
        onResponseError({response, request}) {
            if (import.meta.prerender) {
                return
            }

            if (response.status === 401) {
                const isAttendanceEndpoint = request?.includes?.('/attendance/clock-in') ||
                                            request?.includes?.('/attendance/clock-out')
                const responseCode = String(response?._data?.code || '')
                const isDeviceCredentialFailure = responseCode.startsWith('DEVICE_CREDENTIAL_') ||
                                                  responseCode === 'DEVICE_TOKEN_REQUIRED'

                if (isAttendanceEndpoint) {
                    console.warn('401 Unauthorized on attendance endpoint - Invalid PIN')
                    return
                }
                if (isDeviceCredentialFailure) {
                    // A device-proof failure must block the operation, but it
                    // must not log the staff member out of unrelated POS work.
                    console.warn('401 Unauthorized on device-bound endpoint')
                    return
                }

                // Use clearSession (state-only) so we don't trigger the
                // store's own /login navigation and race with ours below.
                if (typeof store.clearSession === 'function') {
                    store.clearSession()
                } else {
                    // Fallback for stores that don't yet expose clearSession
                    // (e.g. pos-ui's user store). logout() handles its own
                    // navigation, so skip the redirectTo append in that case.
                    store.logout()
                    return
                }
                const route = useRoute()
                // Only force the login page inside auth-required sections.
                // On public pages (landing, /order/*) a stale token's 401
                // from a background call must not eject the visitor — clear
                // the dead session and let them continue as a guest.
                const protectedPrefixes = ['/my', '/business', '/super', '/admin']
                const inProtectedSection = protectedPrefixes.some(
                    prefix => route.path === prefix || route.path.startsWith(prefix + '/')
                )
                if (!inProtectedSection) {
                    console.warn('401 Unauthorized on public page - cleared stale session, staying put')
                    return
                }
                console.warn('401 Unauthorized - Redirecting to login')
                const candidate = route.fullPath !== '/' && route.fullPath !== '/login' ? route.fullPath : undefined
                const redirectTo = SiteUtil.safeInternalRedirect(candidate)
                navigateTo({
                    path: '/login',
                    query: {
                        ...(redirectTo && {redirectTo}),
                    },
                }, {replace: true})
            }
        }
    }

    if (options?.method?.toUpperCase() === 'POST' || options?.method?.toUpperCase() === 'PUT' || options?.method?.toUpperCase() === 'DELETE' || options?.method?.toUpperCase() === 'PATCH') {
        defaults['body'] = body
    }

    if (option !== 3) {
        defaults['headers']['Content-Type'] = contentType
    }

    // for nice deep defaults, please use unjs/defu
    const params = defu(options, defaults)
    if (option === 1 || option === 3)
        return useFetch(url, params)
    else
        return $fetch(url, params)
}
